// Plan 24b, Task 1.2 — Treasure Chest dialog (Screen 14)
//
// Shown when the local player lands on the Treasure Chest node and may buy a
// trophy. If the player has enough keys it is interactive; otherwise the Buy
// button is visually disabled.
//
// ESC / Skip sends `skip_trophy`; Buy sends `buy_trophy`. The dialog waits for
// the resulting server `state_sync` before navigating back to 'board-hud' so a
// stale chest-phase sync cannot remount the dialog after a successful click.
//
// createButton is used for both CTAs so button-states animations,
// hover/pressed feedback, and dispose() cleanup work consistently.

import { deserializeGameState } from '@shared/protocol/serialize';
import type { ServerMessage } from '@shared/protocol';
import type { ScreenHandle, ScreenMountContext } from './types';
import type { BoardMountPayload } from './board-payload';
import { createButton } from '../components/button';
import type { UIComponentHandle } from '../components/types';
import { createBoardTopTimer } from './board-timer';

// ─────────────────────────────────────────────────────────────────────────────
// Token constants — exact values from docs/design/tokens.json
// ─────────────────────────────────────────────────────────────────────────────

/** color.ui.panel-bg-alpha = rgba(30, 35, 50, 0.82) — raised to ~0.95 for dialog */
const DIALOG_BG = 'rgba(30, 35, 50, 0.95)';

/** color.hud.trophy-gold = #FFD700 */
const TROPHY_GOLD = '#FFD700';

/** color.text.primary = #FFFFFF */
const WHITE = '#FFFFFF';

/** color.text.danger = #E04040 */
const DANGER_TEXT = '#E04040';

/** green resource text, tuned to the reference */
const HAVE_GREEN = '#64E870';

/** border-radius.lg = 12px */
const RADIUS_LG = '12px';

/** shadow.panel-heavy = 0 8px 24px rgba(0,0,0,0.60) */
const SHADOW_PANEL_HEAVY = '0 8px 24px rgba(0,0,0,0.60)'; // shadow.panel-heavy

/** Above the dev-only god-mode panel (10000) so the modal remains visible in smoke runs. */
const Z_INDEX_CHEST_MODAL = '10050';
const Z_INDEX_CHEST_HOST = '10050';

/** typography.family.body */
const BODY_FAMILY = "'Nunito Sans', 'Nunito', 'Arial Rounded MT Bold', sans-serif";

/** typography.family.headline */
const HEADLINE_FAMILY = "'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif"; // typography.family.headline

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createTreasureChestScreen(
  ctx: ScreenMountContext<BoardMountPayload>,
): ScreenHandle {
  const { gsapRegistry, host, router, payload } = ctx;
  const previousHostZIndex = host.style.zIndex;

  const cost = payload.state.settings.trophyCost;
  const playerKeys = payload.state.players.get(payload.localPlayerId)!.keys;
  const canAfford = playerKeys >= cost;

  let disposed = false;
  let latestState = payload.state;
  /** Prevents double-send on rapid double-click. */
  let choiceMade = false;
  let actionControlsLocked = payload.isActionControlLocked?.() ?? false;
  let closeTraceSent = false;

  // ── Button handles — kept for dispose() cleanup ───────────────────────────
  let buyBtn: UIComponentHandle | null = null;
  let skipBtn: UIComponentHandle | null = null;
  let unsubscribe: (() => void) | null = null;
  let unsubscribeActionLock: (() => void) | null = null;

  // The app-level #ui-container normally sits below dev overlays. While this
  // modal is open, lift the host stacking context so ?god=1 controls cannot
  // visually cover the chest actions.
  host.style.zIndex = Z_INDEX_CHEST_HOST;

  // ── Root overlay ───────────────────────────────────────────────────────────

  const root = document.createElement('div');
  root.className = 'screen-treasure-chest';
  root.setAttribute('data-screen-id', 'treasure-chest');
  root.style.cssText = [
    'position: fixed',
    'inset: 0',
    `z-index: ${Z_INDEX_CHEST_MODAL}`,
    'display: flex',
    'align-items: center',
    'justify-content: center',
    'pointer-events: none',
  ].join('; ');

  const boardTimer = createBoardTopTimer(payload, { subscribe: false });
  root.appendChild(boardTimer.element);

  // ── Dialog panel — split reference layout ─────────────────────────────────

  const dialog = document.createElement('div');
  dialog.className = 'treasure-chest-dialog';
  dialog.style.cssText = [
    'width: 820px',
    'max-width: calc(100vw - 32px)',
    `background: ${DIALOG_BG}`,
    'border: 2px solid rgba(255,255,255,0.12)',
    `border-radius: ${RADIUS_LG}`,
    'padding: 26px',
    `box-shadow: ${SHADOW_PANEL_HEAVY}`,
    'pointer-events: auto',
    'display: flex',
    'flex-wrap: wrap',
    'align-items: center',
    'justify-content: center',
    'gap: 28px',
    'box-sizing: border-box',
  ].join('; ');

  // ── Left illustration: stylized 2D glowing chest ──────────────────────────

  const artCol = document.createElement('div');
  artCol.className = 'treasure-chest-art';
  artCol.style.cssText = [
    'flex: 1 1 320px',
    'min-width: 260px',
    'display: flex',
    'align-items: center',
    'justify-content: center',
    'user-select: none',
  ].join('; ');

  const illustration = createChestIllustration();
  artCol.appendChild(illustration);

  // ── Right content column ──────────────────────────────────────────────────

  const contentCol = document.createElement('div');
  contentCol.className = 'treasure-chest-content';
  contentCol.style.cssText = [
    'flex: 1 1 300px',
    'min-width: 260px',
    'max-width: 360px',
    'display: flex',
    'flex-direction: column',
    'align-items: stretch',
    'gap: 14px',
  ].join('; ');

  // ── Title "🏆 Treasure Chest!" ─────────────────────────────────────────────

  const titleEl = document.createElement('div');
  titleEl.className = 'treasure-chest-title';
  titleEl.textContent = '🏆 Treasure Chest!';
  titleEl.style.cssText = [
    `font-family: ${HEADLINE_FAMILY}`,
    'font-size: clamp(26px, 4vw, 34px)',
    'font-weight: 700',
    `color: ${WHITE}`,
    'line-height: 1.08',
    'text-align: left',
    `text-shadow: 0 3px 0 #000000, 0 6px 14px rgba(0,0,0,0.60)`,
    'user-select: none',
  ].join('; ');

  // ── Cost row: "Cost: {cost} 🔑s" ──────────────────────────────────────────

  const costEl = document.createElement('div');
  costEl.className = 'treasure-chest-cost';
  costEl.textContent = `Cost: ${cost} 🔑s`;
  costEl.style.cssText = [
    `font-family: ${BODY_FAMILY}`,
    'font-size: 22px',
    'font-weight: 900',
    `color: ${WHITE}`,
    'line-height: 1.1',
    'text-align: left',
    `text-shadow: 0 2px 0 #000000`,
    'user-select: none',
  ].join('; ');

  // ── Have row: "You have: {playerKeys} keys" ───────────────────────────────

  const haveEl = document.createElement('div');
  haveEl.className = 'treasure-chest-have';
  haveEl.textContent = `You have: ${playerKeys} keys`;
  haveEl.style.cssText = [
    `font-family: ${BODY_FAMILY}`,
    'font-size: 22px',
    'font-weight: 900',
    `color: ${canAfford ? HAVE_GREEN : DANGER_TEXT}`,
    'line-height: 1.1',
    'text-align: left',
    `text-shadow: 0 2px 0 #000000`,
    'user-select: none',
  ].join('; ');

  // ── Buy Trophy button (Primary, full-width) ────────────────────────────────
  //
  // When NOT affordable: disabled=true, opacity 0.5, pointerEvents none.
  // These overrides are applied AFTER createButton returns so they sit on top
  // of the button's own opacity rule (which is 0.55 when disabled — we
  // override to exactly 0.5 per spec).

  const buyBtnWrapper = document.createElement('div');
  buyBtnWrapper.className = 'treasure-chest-buy';
  buyBtnWrapper.style.cssText = 'width: 100%; margin-top: 8px';

  buyBtn = createButton({
    gsapRegistry,
    mount: buyBtnWrapper,
    variant: 'primary',
    size: 'large',
    label: '★ Buy Trophy',
    disabled: !canAfford,
    onClick: handleBuy,
  });

  // Apply the spec-mandated overrides for the disabled/enabled states.
  // Cast to HTMLButtonElement since createButton always returns a <button>.
  const buyBtnEl = buyBtn.element as HTMLButtonElement;
  buyBtnEl.style.width = '100%';
  if (!canAfford) {
    buyBtnEl.disabled = true;
    buyBtnEl.style.opacity = '0.5';
    buyBtnEl.style.pointerEvents = 'none';
  } else {
    // Ensure opacity is 1.0 when affordable (createButton may set 0.55 if
    // it receives disabled:false, but we want explicit 1.0 per spec).
    buyBtnEl.style.opacity = '1';
    buyBtnEl.style.pointerEvents = 'auto';
  }

  // ── Skip button (secondary, bordered) ─────────────────────────────────────

  const skipBtnWrapper = document.createElement('div');
  skipBtnWrapper.className = 'treasure-chest-skip';
  skipBtnWrapper.style.cssText = 'width: 100%';

  skipBtn = createButton({
    gsapRegistry,
    mount: skipBtnWrapper,
    variant: 'secondary',
    size: 'large',
    label: 'Skip',
    onClick: handleSkip,
  });

  const skipBtnEl = skipBtn.element as HTMLButtonElement;
  skipBtnEl.style.width = '100%';
  skipBtnEl.style.background =
    'linear-gradient(to bottom, rgba(70,76,86,0.96), rgba(38,43,52,0.96))';
  skipBtnEl.style.border = '2px solid rgba(255,255,255,0.34)';
  skipBtnEl.style.boxShadow = '0 4px 0 rgba(0,0,0,0.55)';

  // ── Assemble DOM ───────────────────────────────────────────────────────────

  contentCol.appendChild(titleEl);
  contentCol.appendChild(costEl);
  contentCol.appendChild(haveEl);
  contentCol.appendChild(buyBtnWrapper);
  contentCol.appendChild(skipBtnWrapper);
  dialog.appendChild(artCol);
  dialog.appendChild(contentCol);
  root.appendChild(dialog);
  host.appendChild(root);
  payload.tracePresentation?.({
    stage: 'modal_open',
    screen: 'treasure-chest',
    encounterKind: 'treasure_chest',
    nodeId: payload.state.pendingBoardEncounter?.nodeId ?? payload.state.treasureChestNodeId,
  });

  // ── Server confirmation/rejection handling ────────────────────────────────

  unsubscribe = payload.subscribe((msg: ServerMessage) => {
    if (disposed) return;

    if (msg.type === 'event' && isChestCompletionEvent(msg)) {
      closeToBoardHud(completionReason(msg));
      return;
    }

    if (msg.type === 'state_sync') {
      latestState = deserializeGameState(msg.state);
      boardTimer.updateFromGameState(latestState);
      if (isLocalChestPrompt(msg)) return;

      closeToBoardHud('state_sync');
      return;
    }

    if (msg.type === 'notification' && isChestChoiceRejection(msg.text)) {
      console.warn(`[treasure-chest] server rejected chest choice: ${msg.text}`);
      choiceMade = false;
      setPending(false);
    }
  });
  unsubscribeActionLock =
    payload.subscribeActionControlLock?.((locked) => {
      if (disposed || actionControlsLocked === locked) return;
      actionControlsLocked = locked;
      setPending(choiceMade);
    }) ?? null;
  setPending(choiceMade);

  // ── ESC key handler (named function for clean deregistration) ─────────────

  function closeToBoardHud(reason: string): void {
    traceModalClose(reason);
    const nextPayload: BoardMountPayload = {
      ...payload,
      state: latestState,
    };
    dispose();
    router.navigate('board-hud', nextPayload);
  }

  function traceModalClose(reason: string): void {
    if (closeTraceSent) return;
    closeTraceSent = true;
    payload.tracePresentation?.({
      stage: 'modal_close',
      screen: 'treasure-chest',
      encounterKind: 'treasure_chest',
      nodeId: latestState.pendingBoardEncounter?.nodeId ?? latestState.treasureChestNodeId,
      reason,
    });
  }

  function handleEsc(e: KeyboardEvent): void {
    if (disposed) return;
    if (e.key === 'Escape') {
      handleSkip();
    }
  }

  document.addEventListener('keydown', handleEsc);

  // ── Action handlers ────────────────────────────────────────────────────────

  function handleBuy(): void {
    if (disposed || choiceMade || actionControlsLocked) return;
    choiceMade = true;
    setPending(true);
    payload.send({ type: 'buy_trophy' });
  }

  function handleSkip(): void {
    if (disposed || choiceMade || actionControlsLocked) return;
    choiceMade = true;
    setPending(true);
    payload.send({ type: 'skip_trophy' });
  }

  function setPending(pending: boolean): void {
    const locked = pending || actionControlsLocked;
    root.dataset.pending = pending ? 'true' : 'false';
    root.dataset.actionLocked = locked ? 'true' : 'false';

    if (canAfford) {
      buyBtnEl.disabled = locked;
      buyBtnEl.style.pointerEvents = locked ? 'none' : 'auto';
      buyBtnEl.style.opacity = locked ? '0.72' : '1';
      buyBtnEl.toggleAttribute('data-action-locked', locked);
    }

    skipBtnEl.disabled = locked;
    skipBtnEl.style.pointerEvents = locked ? 'none' : 'auto';
    skipBtnEl.style.opacity = locked ? '0.72' : '1';
    skipBtnEl.toggleAttribute('data-action-locked', locked);
  }

  function isLocalChestPrompt(msg: Extract<ServerMessage, { type: 'state_sync' }>): boolean {
    const activeId = msg.state.turnOrder[msg.state.currentPlayerIndex] ?? null;
    return msg.state.phase === 'chest_phase' && activeId === payload.localPlayerId;
  }

  function isChestCompletionEvent(msg: Extract<ServerMessage, { type: 'event' }>): boolean {
    const event = msg.event;
    if (event.type === 'phase_change' && event.fromPhase === 'chest_phase') return true;
    return (
      event.type === 'economy_change' &&
      event.playerId === payload.localPlayerId &&
      event.reason === 'trophy_purchase'
    );
  }

  function completionReason(msg: Extract<ServerMessage, { type: 'event' }>): string {
    return msg.event.type === 'phase_change' ? 'phase_change' : 'trophy_purchase';
  }

  function isChestChoiceRejection(text: string): boolean {
    return (
      text === 'no_room' ||
      text === 'not_your_turn' ||
      text === 'not_at_chest' ||
      text === 'cannot_purchase' ||
      text === 'purchase_failed' ||
      text.startsWith('wrong_phase:')
    );
  }

  // ── dispose ────────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    traceModalClose('dispose');

    document.removeEventListener('keydown', handleEsc);
    unsubscribe?.();
    unsubscribe = null;
    unsubscribeActionLock?.();
    unsubscribeActionLock = null;

    buyBtn?.dispose();
    buyBtn = null;
    skipBtn?.dispose();
    skipBtn = null;
    boardTimer.dispose();

    if (root.parentNode) {
      root.parentNode.removeChild(root);
    }

    host.style.zIndex = previousHostZIndex;
  }

  return { element: root, dispose };
}

function createChestIllustration(): HTMLElement {
  const art = document.createElement('div');
  art.className = 'treasure-chest-illustration';
  art.style.cssText = [
    'position: relative',
    'width: min(330px, 70vw)',
    'aspect-ratio: 1 / 1',
    'display: flex',
    'align-items: center',
    'justify-content: center',
  ].join('; ');

  const glow = document.createElement('div');
  glow.style.cssText = [
    'position: absolute',
    'inset: 8%',
    'border-radius: 50%',
    'background: radial-gradient(circle, rgba(255,229,118,0.95) 0%, rgba(255,190,56,0.55) 36%, rgba(255,190,56,0.12) 62%, rgba(255,190,56,0) 76%)',
    'box-shadow: 0 0 38px rgba(255,212,64,0.70), 0 0 92px rgba(255,212,64,0.42)',
  ].join('; ');
  art.appendChild(glow);

  const rays = document.createElement('div');
  rays.style.cssText = [
    'position: absolute',
    'inset: 0',
    'border-radius: 50%',
    'background: conic-gradient(from 10deg, rgba(255,255,255,0.00), rgba(255,255,255,0.34), rgba(255,255,255,0.00) 11%, rgba(255,255,255,0.00) 21%, rgba(255,255,255,0.28) 25%, rgba(255,255,255,0.00) 31%, rgba(255,255,255,0.00) 44%, rgba(255,255,255,0.32) 49%, rgba(255,255,255,0.00) 55%, rgba(255,255,255,0.00) 70%, rgba(255,255,255,0.28) 75%, rgba(255,255,255,0.00) 82%)',
    'opacity: 0.8',
    'filter: blur(0.5px)',
  ].join('; ');
  art.appendChild(rays);

  const chest = document.createElement('div');
  chest.style.cssText = [
    'position: relative',
    'width: 210px',
    'height: 150px',
    'z-index: 1',
    'filter: drop-shadow(0 12px 12px rgba(0,0,0,0.45))',
  ].join('; ');

  const lid = document.createElement('div');
  lid.style.cssText = [
    'position: absolute',
    'left: 18px',
    'top: 8px',
    'width: 174px',
    'height: 70px',
    'border-radius: 56px 56px 10px 10px',
    'background: linear-gradient(145deg, #C57931 0%, #8F4B24 58%, #5A2B19 100%)',
    'border: 5px solid #3B2117',
    'box-sizing: border-box',
    'overflow: hidden',
  ].join('; ');

  const lidBand = document.createElement('div');
  lidBand.style.cssText = [
    'position: absolute',
    'left: 16px',
    'right: 16px',
    'top: 16px',
    'height: 12px',
    'border-radius: 999px',
    'background: linear-gradient(to bottom, #FFE08A, #C7881F)',
    'box-shadow: 0 2px 0 #4F2C12',
  ].join('; ');
  lid.appendChild(lidBand);

  const base = document.createElement('div');
  base.style.cssText = [
    'position: absolute',
    'left: 10px',
    'top: 62px',
    'width: 190px',
    'height: 82px',
    'border-radius: 10px 10px 18px 18px',
    'background: linear-gradient(160deg, #B8642B 0%, #8B401F 68%, #5A2B19 100%)',
    'border: 5px solid #3B2117',
    'box-sizing: border-box',
    'overflow: hidden',
  ].join('; ');

  const baseBand = document.createElement('div');
  baseBand.style.cssText = [
    'position: absolute',
    'left: 0',
    'right: 0',
    'top: 12px',
    'height: 14px',
    'background: linear-gradient(to bottom, #FFE08A, #C7881F)',
    'box-shadow: 0 2px 0 #4F2C12',
  ].join('; ');
  base.appendChild(baseBand);

  const lock = document.createElement('div');
  lock.style.cssText = [
    'position: absolute',
    'left: 50%',
    'top: 62px',
    'width: 34px',
    'height: 42px',
    'transform: translateX(-50%)',
    'border-radius: 8px',
    'background: linear-gradient(to bottom, #FFE48E, #D59625)',
    'border: 4px solid #4F2C12',
    'box-sizing: border-box',
    'z-index: 2',
  ].join('; ');

  const coins = document.createElement('div');
  coins.textContent = '● ● ● ● ●';
  coins.style.cssText = [
    'position: absolute',
    'left: 42px',
    'top: 52px',
    `color: ${TROPHY_GOLD}`,
    'font-size: 22px',
    'letter-spacing: -2px',
    'text-shadow: 0 2px 0 #8A5A10, 0 0 12px rgba(255,215,0,0.9)',
    'z-index: 3',
  ].join('; ');

  chest.appendChild(lid);
  chest.appendChild(base);
  chest.appendChild(coins);
  chest.appendChild(lock);
  art.appendChild(chest);

  const sparkles: Array<[string, string, string]> = [
    ['✦', '10%', '34%'],
    ['✦', '78%', '28%'],
    ['✧', '18%', '70%'],
    ['✦', '72%', '76%'],
  ];
  for (const [glyph, left, top] of sparkles) {
    const sparkle = document.createElement('div');
    sparkle.textContent = glyph;
    sparkle.style.cssText = [
      'position: absolute',
      `left: ${left}`,
      `top: ${top}`,
      'z-index: 2',
      'font-size: 30px',
      `color: ${WHITE}`,
      'text-shadow: 0 0 12px rgba(255,255,255,0.95), 0 0 18px rgba(255,215,0,0.85)',
      'transform: translate(-50%, -50%)',
    ].join('; ');
    art.appendChild(sparkle);
  }

  return art;
}
